import React, { useMemo, useState } from 'react';
import {
  Folder as FolderIcon,
  File as FileIcon,
  MoreVertical,
  Download,
  Pencil,
  Trash2,
  FolderInput,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import type { FileFolder, ProjectFile } from '../types';
import { RenameFolderDialog } from './RenameFolderDialog';

export interface FileBrowserSelection {
  folderIds: string[];
  fileUuids: string[];
}

interface FileBrowserProps {
  folders: FileFolder[];
  files: ProjectFile[];
  isLoading?: boolean;
  selection: FileBrowserSelection;
  onSelectionChange: (selection: FileBrowserSelection) => void;
  onOpenFolder: (folder: FileFolder) => void;
  onRenameFolder: (folder: FileFolder, name: string) => Promise<void> | void;
  onDeleteFolder: (folder: FileFolder) => void;
  onDeleteFile: (file: ProjectFile) => void;
  onMoveFolder: (folder: FileFolder) => void;
  onMoveFile: (file: ProjectFile) => void;
}

const formatBytes = (size: number | null): string => {
  if (size === null || size === undefined) return '—';
  if (size === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(1024));
  const value = size / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (value: string) => new Date(value).toLocaleDateString();

export const FileBrowser: React.FC<FileBrowserProps> = ({
  folders,
  files,
  isLoading = false,
  selection,
  onSelectionChange,
  onOpenFolder,
  onRenameFolder,
  onDeleteFolder,
  onDeleteFile,
  onMoveFolder,
  onMoveFile,
}) => {
  const [renameTarget, setRenameTarget] = useState<FileFolder | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const totalRows = folders.length + files.length;

  const selectedFolderSet = useMemo(() => new Set(selection.folderIds), [selection.folderIds]);
  const selectedFileSet = useMemo(() => new Set(selection.fileUuids), [selection.fileUuids]);

  const isAllSelected =
    totalRows > 0 &&
    folders.every((folder) => selectedFolderSet.has(folder.id)) &&
    files.every((file) => selectedFileSet.has(file.uuid));
  const isSomeSelected =
    !isAllSelected &&
    (selection.folderIds.length > 0 || selection.fileUuids.length > 0);

  const toggleAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange({
        folderIds: Array.from(
          new Set([...selection.folderIds, ...folders.map((folder) => folder.id)]),
        ),
        fileUuids: Array.from(
          new Set([...selection.fileUuids, ...files.map((file) => file.uuid)]),
        ),
      });
    } else {
      const visibleFolderIds = new Set(folders.map((folder) => folder.id));
      const visibleFileUuids = new Set(files.map((file) => file.uuid));
      onSelectionChange({
        folderIds: selection.folderIds.filter((id) => !visibleFolderIds.has(id)),
        fileUuids: selection.fileUuids.filter((uuid) => !visibleFileUuids.has(uuid)),
      });
    }
  };

  const toggleFolder = (id: string, checked: boolean) => {
    const next = new Set(selection.folderIds);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectionChange({ folderIds: Array.from(next), fileUuids: selection.fileUuids });
  };

  const toggleFile = (uuid: string, checked: boolean) => {
    const next = new Set(selection.fileUuids);
    if (checked) next.add(uuid);
    else next.delete(uuid);
    onSelectionChange({ folderIds: selection.folderIds, fileUuids: Array.from(next) });
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  aria-label="Select all"
                  checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                  onCheckedChange={(checked) => toggleAll(checked === true)}
                  disabled={totalRows === 0}
                />
              </TableHead>
              <TableHead className="w-[50%]">Name</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded By</TableHead>
              <TableHead>Modified</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  Loading files...
                </TableCell>
              </TableRow>
            ) : totalRows === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  This folder is empty. Upload files or create a subfolder to get started.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {folders.map((folder) => {
                  const isSelected = selectedFolderSet.has(folder.id);
                  return (
                    <TableRow
                      key={`folder-${folder.id}`}
                      data-state={isSelected ? 'selected' : undefined}
                      className="cursor-default hover:bg-muted/50"
                      onDoubleClick={() => onOpenFolder(folder)}
                    >
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${folder.name}`}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            toggleFolder(folder.id, checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => onOpenFolder(folder)}
                          className="flex items-center gap-2 hover:text-primary text-left"
                        >
                          <FolderIcon className="h-4 w-4 text-primary" />
                          <span>{folder.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({folder.subfolder_count + folder.file_count})
                          </span>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">—</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {folder.created_by
                          ? `${folder.created_by.first_name} ${folder.created_by.last_name}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(folder.updated_at)}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setRenameTarget(folder)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onMoveFolder(folder)}>
                              <FolderInput className="mr-2 h-4 w-4" />
                              Move
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDeleteFolder(folder)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {files.map((file) => {
                  const isSelected = selectedFileSet.has(file.uuid);
                  return (
                    <TableRow
                      key={`file-${file.uuid}`}
                      data-state={isSelected ? 'selected' : undefined}
                      className="hover:bg-muted/50"
                    >
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          aria-label={`Select ${file.original_file_name}`}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            toggleFile(file.uuid, checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <a
                          href={file.file_url ?? '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 hover:text-primary"
                        >
                          <FileIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{file.original_file_name}</span>
                        </a>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatBytes(file.file_size)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {file.uploaded_by
                          ? `${file.uploaded_by.first_name} ${file.uploaded_by.last_name}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(file.updated_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {file.file_url && (
                              <DropdownMenuItem asChild>
                                <a
                                  href={file.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={file.original_file_name}
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  Download
                                </a>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onMoveFile(file)}>
                              <FolderInput className="mr-2 h-4 w-4" />
                              Move
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDeleteFile(file)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {renameTarget && (
        <RenameFolderDialog
          open={!!renameTarget}
          currentName={renameTarget.name}
          isSubmitting={isRenaming}
          onClose={() => {
            setRenameTarget(null);
            setIsRenaming(false);
          }}
          onRename={async (name) => {
            setIsRenaming(true);
            try {
              await onRenameFolder(renameTarget, name);
            } finally {
              setIsRenaming(false);
            }
          }}
        />
      )}
    </>
  );
};
